import React, { useState, useEffect, useCallback } from "react";
import { useEmployerJobApi } from "../../../_common/hooks/actions/job/appEmployerJobApiHook";
import BulkResumeUploadModal from "./BulkResumeUploadModal";
import ViewProfileModal from "../_common/ViewProfileModal";
import SearchBox from "../_common/SearchBox";
import ProgressIndicator from "../_common/ProgressIndicator";
import { useJobListSelector } from "src/_common/hooks/selectors/jobSelector.";
import { CANDIDATE_STATUS, REFRESH_INTERVAL } from "src/_config";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface TalentScoutingProps {
  jobId?: string;
}

interface Candidate {
  id: string;
  jobseekerId?: number;
  name: string;
  avatar: string;
  position: string;
  status: string;
  type: number; // Store raw type number instead of translated string
  get_new_data?: number; // Flag to indicate if this is a new candidate
  location?: string;
  experience?: string;
  gender?: string;
  salary?: string;
  degree?: string;
  photo?: string;
  candidateDetails?: any; // Store full candidate details for ViewProfileModal
}

export default function TalentScouting({ jobId }: TalentScoutingProps) {
  const { t } = useTranslation("job-details");
  const [currentPage, setCurrentPage] = useState(1);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isViewProfileOpen, setIsViewProfileOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(false);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false); // Separate state for refresh operations
  const [extraValueAvailable, setExtraValueAvailable] = useState(false); // State for extra value availability

  const jobListings = useJobListSelector();
  const currentJob = jobListings?.find((job) => job.emp_job_id == jobId);

  // Pagination constants
  const ITEMS_PER_PAGE = 10;

  // Hook for API calls
  const jobApi = useEmployerJobApi();

  // Helper function to calculate total pages
  const getTotalPages = () => Math.ceil(totalCandidates / ITEMS_PER_PAGE);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= getTotalPages()) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < getTotalPages()) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const totalPages = getTotalPages();

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Auto-refresh countdown timer
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isAutoRefreshEnabled) {
      interval = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            // Time's up, refresh the data and reset countdown
            if (jobId) {
              fetchJobCandidates(true); // Pass true to indicate it's a refresh operation
            }
            return REFRESH_INTERVAL; // Reset to 2 minutes
          }
          return prevCountdown - 1;
        });
      }, 1000); // Update every second
    } else {
      // Reset countdown when auto-refresh is disabled
      setCountdown(REFRESH_INTERVAL);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isAutoRefreshEnabled, jobId]);

  const fetchJobCandidates = (isRefreshOperation = false) => {
    if (!jobId) return;

    const offset = (currentPage - 1) * ITEMS_PER_PAGE;

    console.log(`Fetching candidates for job ${jobId}: page ${currentPage}, limit ${ITEMS_PER_PAGE}, offset ${offset}`);

    // Use different loading state based on operation type
    if (isRefreshOperation) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    // Prepare additional parameters for search
    const additionalParams: any = {};
    if (searchTerm && searchTerm.trim()) {
      additionalParams.search = searchTerm.trim();
    }
    additionalParams.status = Object.values(CANDIDATE_STATUS).filter((status) => status !== CANDIDATE_STATUS.HIRED);
    additionalParams.matching_percentage = 0;

    jobApi.callGetScreening(
      jobId,
      ITEMS_PER_PAGE,
      offset,
      additionalParams,
      (message: string, data: any) => {
        setIsLoading(false);
        setIsRefreshing(false);

        if (data && data.screenings) {
          // Transform API response to match the component interface
          console.log("Fetched screening data:", data);
          const transformedCandidates = data.screenings.map((screening: any) => {
            const jobseekerDetail = screening.jobseeker_details?.[0];
            const basicDetails = jobseekerDetail?.basic_details?.[0];

            // Get candidate name from the basic details structure
            let candidateName = "";
            if (basicDetails?.name && basicDetails.name.trim()) {
              candidateName = basicDetails.name.trim();
            } else if (basicDetails?.first_name && basicDetails?.last_name) {
              candidateName = `${basicDetails.first_name} ${basicDetails.last_name}`.trim();
            } else if (basicDetails?.first_name) {
              candidateName = basicDetails.first_name.trim();
            } else {
              candidateName = "N/A";
            }

            // Get current designation from experience
            const currentDesignation =
              jobseekerDetail?.experience?.[0]?.designation || basicDetails?.current_designation || "-";

            // Get current location
            const currentLocation = basicDetails?.current_location || "";

            // Get total experience
            const totalExperienceYear = basicDetails?.total_experience_year || "";

            // Get gender
            const genderMap: { [key: number]: string } = {
              1: "Male",
              2: "Female",
            };
            const gender = basicDetails?.gender_id ? genderMap[basicDetails.gender_id] || "-" : "-";

            // Get salary
            const currentSalary = basicDetails?.current_salary || "";
            const currentSalaryCurrency = basicDetails?.current_salary_currency || "";

            let detailsToSet = {
              id: screening.id,
              jobseekerId: screening.jobseeker_id,
              name: candidateName,
              avatar:
                screening.type == 1 || screening.type == 2 ? screening?.document_full_path?.profile_pic || null : null,
              position:
                jobseekerDetail?.experience
                  ?.map((exp: any) => exp.designation)
                  .filter(Boolean)
                  .join(", ") || "-",
              // status: screening.latest_screening_history ? "Screened" : "Not Screened",
              resume_file:
                screening.type == 1 || screening.type == 2
                  ? screening?.document_full_path?.system_resume_file ||
                    screening?.document_full_path?.resume_file ||
                    null
                  : null,
              video_file:
                screening.type == 1 || screening.type == 2 ? screening?.document_full_path?.video_file || null : null,
              status: screening.status == CANDIDATE_STATUS.REJECTED ? "Rejected" : "Screened",
              type: screening.type, // Store raw type number
              get_new_data: screening.get_new_data,
            };

            // Transform data for ViewProfileModal compatibility
            const candidateDetailsForModal = {
              ...detailsToSet,
              currentDesignation,
              currentLocation,
              totalExperienceYear,
              gender,
              currentSalary,
              currentSalaryCurrency,
              skills: basicDetails?.key_skills?.join(", ") || "",
              profileSummary: basicDetails?.profile_summary || "",
              employmentDetails: jobseekerDetail?.experience || [],
              educationDetails: jobseekerDetail?.education || [],
              certificationDetails: jobseekerDetail?.certifications || [],
              courseDetails: jobseekerDetail?.courses || [],
              projectDetails: jobseekerDetail?.projects || [],
              languageDetails: jobseekerDetail?.languages || [],
              hobbyDetails: jobseekerDetail?.hobbies || [],
              referenceDetails: jobseekerDetail?.references || [],
              portfolioDetails: jobseekerDetail?.portfolio || [],
              socialMediaDetails: jobseekerDetail?.social_media || [],
              basicDetails: {
                ...basicDetails,
                resume_file: screening?.document_full_path?.system_resume_file || null,
                video_file: screening?.document_full_path?.video_file || null,
              },
              // Public profile information
              jobseeker_public_profiles: jobseekerDetail?.jobseeker_public_profiles || [],
            };

            return {
              ...detailsToSet,
              candidateDetails: candidateDetailsForModal, // Store full details for ViewProfileModal
            };
          });

          setCandidates(transformedCandidates);

          if (data.pagination) {
            setTotalCandidates(data.pagination.total || 0);
          }

          // Set extra value available flag
          setExtraValueAvailable(data.extra_value_available || false);
        } else {
          setCandidates([]);
          setTotalCandidates(0);
          setExtraValueAvailable(false);
        }
      },
      (errorMessage: string) => {
        // Error callback
        console.error("Failed to fetch candidates:", errorMessage);
        setIsLoading(false);
        setIsRefreshing(false);
        // Clear candidates on error
        setCandidates([]);
        setTotalCandidates(0);
      }
    );
  };

  // Reset state when jobId changes
  useEffect(() => {
    if (jobId) {
      setCurrentPage(1);
      setSearchTerm("");
      setCandidates([]);
      setTotalCandidates(0);
      setIsAutoRefreshEnabled(false);
      setCountdown(120);
      setSelectedCandidate(null);
    }
  }, [jobId]);

  // Fetch candidates when jobId, currentPage, or searchTerm changes
  useEffect(() => {
    if (jobId) {
      fetchJobCandidates();
    }
  }, [jobId, currentPage, searchTerm]);

  const handleViewProfile = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setIsViewProfileOpen(true);
  };

  const handleBulkUploadOpen = () => {
    setIsBulkUploadOpen(true);
  };

  const handleBulkUploadClose = () => {
    setIsBulkUploadOpen(false);
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setCurrentPage(1); // Reset to first page when searching
    // The useEffect will trigger fetchJobCandidates when searchTerm changes
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1); // Reset to first page when clearing search
  };

  const handleAutoRefreshToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAutoRefreshEnabled(e.target.checked);
    if (e.target.checked) {
      setCountdown(REFRESH_INTERVAL); // Reset countdown when enabling
    }
  };

  const formatCountdown = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    console.log("TalentScouting component mounted", selectedCandidate);
  }, [selectedCandidate]);

  return (
    <div className="talent-scouting-wrapper">
      {/* Header */}
      <div className="talent-scouting-header">
        <div className="header-left-ai-job">
          <div className="job-overview-section">
            <h1 className="job-overview-title">{currentJob?.job_title}</h1>
            <p className="job-overview-subtitle">{t("jobDetails.talentScouting.subtitle")}</p>
          </div>
        </div>
        {/* Only show header-right when there are candidates or search is active */}
        {(candidates?.length > 0 || searchTerm || isLoading) && (
          <div className="header-right">
            <SearchBox
              placeholder={t("jobDetails.talentScouting.searchPlaceholder")}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />
            <button className="btn-preview" onClick={handleBulkUploadOpen}>
              <i className="fa fa-upload" style={{ marginRight: "10px" }}></i>
              {t("jobDetails.talentScouting.bulkUploadButton")}
            </button>
          </div>
        )}
      </div>

      {/* Show ProgressIndicator when no candidates and no search */}
      {!isLoading && candidates?.length === 0 && !searchTerm ? (
        <ProgressIndicator stage="talent-scouting" />
      ) : (
        <div className="talent-scouting-inner-wrapper">
          {/* Position Filter Section */}
          <div className="filter-section">
            <div className="position-info">
              <h2 className="position-title">{currentJob?.job_title}</h2>
              <p className="candidate-count-text">
                <span className="count-number">{totalCandidates}</span>{" "}
                {searchTerm ? t("jobDetails.talentScouting.matching") : t("jobDetails.talentScouting.potential")}{" "}
                {t("jobDetails.talentScouting.candidatesFound")}{" "}
                {searchTerm && `${t("jobDetails.talentScouting.for")} "${searchTerm}"`}
              </p>
            </div>
            {isRefreshing && (
              <div className="refresh-loader-overlay">
                <div className="loading-spinner"></div>
                <span className="refresh-text">{t("jobDetails.talentScouting.refreshing")}</span>
              </div>
            )}
            <div className="filter-actions">
              {isAutoRefreshEnabled && (
                <div className="countdown-display">
                  <span className="countdown-text">
                    {t("jobDetails.talentScouting.nextRefresh")}: {formatCountdown(countdown)}
                  </span>
                </div>
              )}
              <button
                className="refresh-btn"
                onClick={() => {
                  fetchJobCandidates(true); // Pass true to indicate it's a refresh operation
                  if (isAutoRefreshEnabled) {
                    setCountdown(REFRESH_INTERVAL); // Reset countdown when manually refreshing
                  }
                }}
                disabled={isRefreshing}
              >
                <i className="fa fa-refresh"></i>
                {t("jobDetails.talentScouting.refresh")}
              </button>
              <div className="auto-refresh-toggle">
                <span className="auto-refresh-text" style={{ fontSize: "14px" }}>
                  {t("jobDetails.talentScouting.autoRefresh")}
                </span>
                <button
                  className={`toggle-switch ${isAutoRefreshEnabled ? "active" : ""}`}
                  onClick={() => {
                    setIsAutoRefreshEnabled(!isAutoRefreshEnabled);
                    if (!isAutoRefreshEnabled) {
                      setCountdown(REFRESH_INTERVAL); // Reset countdown when enabling
                    }
                  }}
                  style={{
                    width: "44px",
                    height: "24px",
                    borderRadius: "12px",
                    border: "none",
                    backgroundColor: isAutoRefreshEnabled ? "#3b82f6" : "#e5e7eb",
                    position: "relative",
                    cursor: "pointer",
                    transition: "background-color 0.3s",
                    marginLeft: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "white",
                      position: "absolute",
                      top: "2px",
                      left: isAutoRefreshEnabled ? "22px" : "2px",
                      transition: "left 0.3s",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Candidates Table */}
          <div className="table-wrapper">
            {!isLoading && candidates?.length === 0 && !searchTerm ? (
              <ProgressIndicator stage="talent-scouting" />
            ) : (
              <table className="candidates-table">
                <thead>
                  <tr>
                    <th className="text-center">{t("jobDetails.talentScouting.table.slNo")}</th>
                    <th>{t("jobDetails.talentScouting.table.candidateName")}</th>
                    <th>{t("jobDetails.talentScouting.table.currentPosition")}</th>
                    <th className="text-center">{t("jobDetails.talentScouting.table.details")}</th>
                    <th className="text-center">{t("jobDetails.talentScouting.table.type")}</th>
                    <th className="text-center">{t("jobDetails.talentScouting.table.screening")}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", padding: "40px" }}>
                        <div className="loading-container">
                          <div className="loading-spinner"></div>
                        </div>
                      </td>
                    </tr>
                  ) : candidates?.length > 0 ? (
                    candidates.map((candidate, index) => (
                      <tr key={candidate.id}>
                        <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                        <td>
                          <div className="candidate-cell">
                            <div className="candidate-avatar">
                              <img
                                src={candidate.avatar || "/images/user-avatar.jpg"}
                                alt="User Avatar"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                            <span className="candidate-name">
                              {candidate.name}
                              {candidate.get_new_data === 1 && <span className="new-badge">New</span>}
                            </span>
                          </div>
                        </td>
                        <td className="position-cell">{candidate.position}</td>
                        <td>
                          <button className="view-profile-btn" onClick={() => handleViewProfile(candidate)}>
                            {t("jobDetails.talentScouting.viewProfile")}
                          </button>
                        </td>
                        <td className="position-cell">
                          {candidate.type === 1
                            ? t("jobDetails.talentScouting.types.applied")
                            : candidate.type === 2
                            ? t("jobDetails.talentScouting.types.recommended")
                            : candidate.type === 3
                            ? t("jobDetails.talentScouting.types.bulkUpload")
                            : ""}
                        </td>
                        <td>
                          <span className={(candidate.status == "Rejected" ? "rejected" : "screening") + "-badge"}>
                            {candidate.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", padding: "20px" }}>
                        {searchTerm
                          ? `${t("jobDetails.talentScouting.noCandidatesMatching")} "${searchTerm}"`
                          : t("jobDetails.talentScouting.noCandidates")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {candidates.length > 0 && getTotalPages() > 1 && (
            <div className="pagination">
              <button className="page-btn" onClick={handlePrevPage} disabled={currentPage === 1}>
                {t("jobDetails.talentScouting.prev")}
              </button>
              <div className="page-numbers">
                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <span key={`ellipsis-${index}`}>...</span>
                  ) : (
                    <button
                      key={page}
                      className={`page-num ${currentPage === page ? "active" : ""}`}
                      onClick={() => handlePageChange(page as number)}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>
              <button className="page-btn" onClick={handleNextPage} disabled={currentPage === getTotalPages()}>
                {t("jobDetails.talentScouting.next")}
              </button>
            </div>
          )}

          {/* Upgrade Plan Message */}
          {extraValueAvailable && (
            <div
              className="upgrade-plan-message"
              style={{
                textAlign: "center",
                padding: "15px 20px",
                marginTop: "20px",
                backgroundColor: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: "8px",
                color: "#856404",
              }}
            >
              <i className="fa fa-info-circle" style={{ marginRight: "8px" }}></i>
              <strong>
                {t("jobDetails.talentScouting.upgradeMessage") ||
                  "More candidates are available! Upgrade your plan to view all matching candidates."}
              </strong>
            </div>
          )}
        </div>
      )}

      {/* Bulk Resume Upload Modal */}
      <BulkResumeUploadModal isOpen={isBulkUploadOpen} onClose={handleBulkUploadClose} jobId={jobId} />

      {/* View Profile Modal */}
      {isViewProfileOpen && jobId && selectedCandidate?.jobseekerId && (
        <ViewProfileModal
          isOpen={isViewProfileOpen}
          onClose={() => setIsViewProfileOpen(false)}
          jobId={jobId}
          jobseekerId={selectedCandidate?.jobseekerId}
          candidateDetails={selectedCandidate?.candidateDetails}
        />
      )}
    </div>
  );
}
